import { useCompliance } from '../contexts/ComplianceContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

const COLOR = {
  green: {
    dot: 'bg-emerald-500',
    ring: 'ring-emerald-500/30',
    badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
    label: 'Complete',
    icon: CheckCircle2,
  },
  yellow: {
    dot: 'bg-amber-400',
    ring: 'ring-amber-400/30',
    badge: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
    label: 'Partially complete',
    icon: AlertTriangle,
  },
  red: {
    dot: 'bg-red-500',
    ring: 'ring-red-500/30',
    badge: 'bg-red-500/15 text-red-400 border-red-500/40',
    label: 'Incomplete',
    icon: XCircle,
  },
};

/**
 * Small inline traffic-light dot shown next to an employee's name across
 * the Admin Portal. Click/hover reveals a popover summarising profile /
 * documents / policies status. Renders nothing when data is missing
 * (e.g. for users who aren't tracked, like admins).
 */
export default function ComplianceIndicator({ userId, size = 'sm', showLabel = false, className = '' }) {
  const { data } = useCompliance(userId);
  if (!data) return null;

  const cfg = COLOR[data.overall] || COLOR.red;
  const dim = size === 'lg' ? 'w-3 h-3' : size === 'md' ? 'w-2.5 h-2.5' : 'w-2 h-2';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
          data-testid={`compliance-indicator-${userId}`}
          aria-label={`Compliance: ${cfg.label}`}
          className={`inline-flex items-center gap-1.5 align-middle rounded-full hover:opacity-90 focus:outline-none focus:ring-2 cursor-pointer ${cfg.ring} ${className}`}
        >
          <span className={`${dim} rounded-full ${cfg.dot} shadow-[0_0_8px_currentColor] inline-block`} />
          {showLabel && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{cfg.label}</span>
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 bg-card border-border p-0 rounded-sm">
        <div className={`px-3 py-2 border-b border-border flex items-center gap-2`}>
          <cfg.icon className={`w-4 h-4 ${data.overall === 'green' ? 'text-emerald-400' : data.overall === 'yellow' ? 'text-amber-400' : 'text-red-400'}`} />
          <span className="text-xs uppercase tracking-wider font-medium">{cfg.label}</span>
        </div>
        <div className="p-3 space-y-2.5 text-xs">
          <Row label="Profile" complete={data.profile.complete} extras={!data.profile.complete ? data.profile.missing.slice(0, 3) : null} />
          <Row label="Required Documents" complete={data.documents.complete} extras={!data.documents.complete ? data.documents.missing.slice(0, 4) : null} />
          <Row label="Legal & Policies" complete={data.policies.complete} extras={!data.policies.complete ? data.policies.missing_titles.slice(0, 4) : null} count={data.policies.total ? `${data.policies.total - data.policies.missing_count}/${data.policies.total}` : null} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, complete, extras, count }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-foreground/80">{label}</span>
        <span className="flex items-center gap-1.5">
          {count && <span className="text-[10px] text-muted-foreground font-mono">{count}</span>}
          {complete ? (
            <span className="inline-flex items-center gap-1 text-emerald-400">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-red-400">
              <XCircle className="w-3 h-3" /> Missing
            </span>
          )}
        </span>
      </div>
      {extras && extras.length > 0 && (
        <ul className="mt-1 ml-3 space-y-0.5 list-disc list-outside text-[10px] text-muted-foreground">
          {extras.map((e, i) => (<li key={i}>{e}</li>))}
        </ul>
      )}
    </div>
  );
}
